import { GameState, IntruderState, IntruderType, AABB, BuildingType } from '../types';
import { INTRUDER_SPEED, TILE, INTRUDER_SIZE } from '../constants';
import { aabbOverlap, dist } from '../collision';

export function updateIntruders(state: GameState, dt: number): void {
  const baseCx = (state.stationGridX + 0.5) * TILE;
  const baseCy = (state.stationGridY + 0.5) * TILE;

  for (const intruder of state.intruders) {
    if (!intruder.alive) continue;

    // Boop knockback animation
    if (intruder.boopTimer > 0) {
      intruder.boopTimer -= dt;
      intruder.x += intruder.boopDx * dt;
      intruder.y += intruder.boopDy * dt;
      if (intruder.boopTimer <= 0) {
        intruder.alive = false;
      }
      continue;
    }

    // Animation timer
    intruder.animTimer += dt;

    // Move toward base
    const dx = baseCx - intruder.x;
    const dy = baseCy - intruder.y;
    const d = Math.sqrt(dx * dx + dy * dy);

    if (d < 30) {
      // Reached base - screen shake and vanish
      intruder.alive = false;
      triggerScreenShake();
      continue;
    }

    const nx = (dx / d) * INTRUDER_SPEED * dt;
    const ny = (dy / d) * INTRUDER_SPEED * dt;

    // Check wall collision
    const newX = intruder.x + nx;
    const newY = intruder.y + ny;
    const half = INTRUDER_SIZE / 2;
    const intruderBox: AABB = { x: newX - half, y: newY - half, w: INTRUDER_SIZE, h: INTRUDER_SIZE };

    let blocked = false;
    for (const b of state.buildings) {
      if (b.type !== BuildingType.Wall) continue;
      const wallBox: AABB = { x: b.gridX * TILE, y: b.gridY * TILE, w: TILE, h: TILE };
      if (aabbOverlap(intruderBox, wallBox)) {
        blocked = true;
        break;
      }
    }

    if (!blocked) {
      intruder.x = newX;
      intruder.y = newY;
    } else {
      // Try to slide around wall
      const slideX = intruder.x + nx;
      const slideBoxX: AABB = { x: slideX - half, y: intruder.y - half, w: INTRUDER_SIZE, h: INTRUDER_SIZE };
      let xBlocked = false;
      for (const b of state.buildings) {
        if (b.type !== BuildingType.Wall) continue;
        const wallBox: AABB = { x: b.gridX * TILE, y: b.gridY * TILE, w: TILE, h: TILE };
        if (aabbOverlap(slideBoxX, wallBox)) { xBlocked = true; break; }
      }
      if (!xBlocked) {
        intruder.x = slideX;
      } else {
        const slideY = intruder.y + ny;
        const slideBoxY: AABB = { x: intruder.x - half, y: slideY - half, w: INTRUDER_SIZE, h: INTRUDER_SIZE };
        let yBlocked = false;
        for (const b of state.buildings) {
          if (b.type !== BuildingType.Wall) continue;
          const wallBox: AABB = { x: b.gridX * TILE, y: b.gridY * TILE, w: TILE, h: TILE };
          if (aabbOverlap(slideBoxY, wallBox)) { yBlocked = true; break; }
        }
        if (!yBlocked) {
          intruder.y = slideY;
        }
      }
    }
  }

  // Remove dead intruders
  state.intruders = state.intruders.filter(i => i.alive);
}

function triggerScreenShake(): void {
  const canvas = document.getElementById('game-canvas');
  if (canvas) {
    canvas.classList.add('shake');
    setTimeout(() => canvas.classList.remove('shake'), 300);
  }
}

export function spawnIntruder(state: GameState): void {
  const edge = Math.floor(Math.random() * 4);
  const cols = state.ground[0].length;
  const rows = state.ground.length;
  let x: number, y: number;

  switch (edge) {
    case 0: // top
      x = Math.random() * cols * TILE;
      y = -20;
      break;
    case 1: // bottom
      x = Math.random() * cols * TILE;
      y = rows * TILE + 20;
      break;
    case 2: // left
      x = -20;
      y = Math.random() * rows * TILE;
      break;
    default: // right
      x = cols * TILE + 20;
      y = Math.random() * rows * TILE;
      break;
  }

  const type = Math.random() > 0.5 ? IntruderType.Slime : IntruderType.WindUpBot;

  state.intruders.push({
    x, y,
    type,
    alive: true,
    boopTimer: 0,
    boopDx: 0,
    boopDy: 0,
    animTimer: Math.random() * 10,
  });
}
